import { ContextConsumer } from '@lit/context';
import { QueryObserver } from '@tanstack/query-core';
import { QueryContext } from './context';
/**
 * QueryController is a class that integrates a query-based data fetching system
 * into a Lit component as a ReactiveController.
 *
 * @template TQueryFnData - The data type returned by the query function.
 * @template TError - The error type for query errors.
 * @template TData - The data type to be used in the component.
 * @template TQueryData - The data type returned by the query (may differ from TData).
 * @template TQueryKey - The query key type.
 */
export class QueryController {
    /**
     * The query client.
     * This can be set manually or using a lit query client context provider.
     */
    set queryClient(queryClient) {
        this._queryClient = queryClient;
        if (queryClient) {
            this.whenQueryClient.resolve(queryClient);
        }
        this.host.requestUpdate();
    }
    get queryClient() {
        return this._queryClient;
    }
    /**
     * Creates a new QueryController instance.
     *
     * @param host - The host component to which this controller is added.
     * @param optionsFn - A function that provides QueryObserverOptions for the query.
     * @param _queryClient - Optionally set the query client.
     * @link [QueryObserverOptions API Docs](). //TODO: Add the correct doc
     */
    constructor(host, optionsFn, _queryClient) {
        this.host = host;
        this.optionsFn = optionsFn;
        this._queryClient = _queryClient;
        /**
         * Promise that is resolved when the query client is set.
         */
        this.whenQueryClient = Promise.withResolvers();
        /**
         * Promise that resolves when the query observer is created.
         */
        this.whenQueryObserver = Promise.withResolvers();
        this.host.addController(this);
        // Initialize the context
        this.context = new ContextConsumer(this.host, {
            context: QueryContext,
            subscribe: true,
            callback: (value) => {
                if (value) {
                    this.queryClient = value;
                }
            },
        });
        // Observe the query if a query function is provided
        if (this.optionsFn) {
            this.observeQuery(this.optionsFn);
        }
        // TODO: allow query client to be supplied, but currently calls `this.host.requestUpdate()` twice
        // if (this._queryClient) {
        //   this.queryClient = this._queryClient;
        // }
    }
    /**
     * Creates a query observer. The query is subscribed whenever the host is connected to the dom.
     *
     * @param options - Options for the query observer
     * @param optimistic - Get an initial optimistic result. Defaults to true.
     */
    async observeQuery(options, optimistic = true) {
        const queryClient = await this.whenQueryClient.promise;
        // Initialize the QueryObserver with defaulted options.
        const defaultedOptions = await this.getDefaultedOptions(typeof options === 'function' ? options() : options);
        this.queryObserver = new QueryObserver(queryClient, defaultedOptions);
        // Get an optimistic result based on the defaulted options.
        if (optimistic) {
            this.result = this.queryObserver.getOptimisticResult(defaultedOptions);
        }
        else {
            this.result = undefined;
        }
        this.host.requestUpdate();
        this.whenQueryObserver.resolve(this.queryObserver);
    }
    /**
     * Invoked when the host component updates.
     * Updates the query observer options with default options if a query function is set.
     */
    async hostUpdate() {
        if (this.optionsFn) {
            const queryObserver = await this.whenQueryObserver.promise;
            // Update options from the options function
            const defaultedOptions = await this.getDefaultedOptions(this.optionsFn());
            queryObserver.setOptions(defaultedOptions);
        }
    }
    /**
     * Invoked when the host component is connected.
     */
    hostConnected() {
        this.subscribe();
    }
    /**
     * Subscribes to the query observer and updates the result.
     */
    async subscribe() {
        const queryObserver = await this.whenQueryObserver.promise;
        // Unsubscribe any previous subscription before subscribing
        this.unsubscribe?.();
        this.unsubscribe = queryObserver.subscribe((result) => {
            this.result = result;
            this.host.requestUpdate();
        });
        queryObserver.updateResult();
        this.host.requestUpdate();
    }
    /**
     * Invoked when the host component is disconnected.
     * Unsubscribes from the query observer to clean up.
     */
    hostDisconnected() {
        this.unsubscribe?.();
        this.unsubscribe = undefined;
    }
    /**
     * Retrieves the default query options by combining the user-provided options
     * with the default options from the query client.
     *
     * @returns The default query options.
     */
    async getDefaultedOptions(options) {
        const queryClient = await this.whenQueryClient.promise;
        return queryClient.defaultQueryOptions(options);
    }
}
